import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useUserToken } from "@/context/auth-context";
import { useCreateInputVariableSet } from "@/queries/input-variable-sets/useCreateInputVariableSet";
import { displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

const useCreateDefaultInputVariableSet = (folderId?: number, openInNewTab = false) => {
  const userToken = useUserToken();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const createInputVariableSetMutation = useCreateInputVariableSet(userToken ?? "");
  const [isCreating, setIsCreating] = useState(false);

  const create = async () => {
    if (!workspaceId) {
      return;
    }

    setIsCreating(true);

    try {
      await new Promise<void>((resolve, reject) => {
        createInputVariableSetMutation.mutate(
          {
            variables: {},
            folder_id: folderId ?? null,
          },
          {
            onSuccess: response => {
              if (!response.success) {
                reject(new Error(response.message || "Error creating input variable set"));
                return;
              }

              const inputVariableSetId = response.input_variable_set?.id;

              if (!inputVariableSetId) {
                reject(new Error("Invalid response from server"));
                return;
              }

              const url = `/workspace/${workspaceId}/input-variable-set/${inputVariableSetId}`;

              if (openInNewTab) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
              resolve();
            },
            onError: error => {
              const getErrorMessage = (err: unknown): string => {
                if (typeof err === "string") {
                  return err;
                }

                if (err instanceof Error) {
                  return err.message;
                }

                return "Error creating input variable set";
              };

              displayToast(getErrorMessage(error), ToastType.error);
              reject(error);
            },
          },
        );
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    create,
    isCreating: isCreating || createInputVariableSetMutation.isPending,
    isError: createInputVariableSetMutation.isError,
    error: createInputVariableSetMutation.error,
  };
};

export default useCreateDefaultInputVariableSet;
